import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Delete as DeleteIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  VpnKey as VpnKeyIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { generateClient } from 'aws-amplify/api';
import { activityByEntityID } from 'graphql/activity';

const API = generateClient();

// Action type configurations
const actionConfig = {
  USER_CREATED: {
    label: 'User Created',
    icon: PersonAddIcon,
    color: 'success',
    severity: 'info'
  },
  USER_UPDATED: {
    label: 'User Updated',
    icon: EditIcon,
    color: 'info',
    severity: 'info'
  },
  USER_ENABLED: {
    label: 'User Enabled',
    icon: LockOpenIcon,
    color: 'success',
    severity: 'success'
  },
  USER_DISABLED: {
    label: 'User Disabled',
    icon: LockIcon,
    color: 'error',
    severity: 'warning'
  },
  USER_DELETED: {
    label: 'User Deleted',
    icon: DeleteIcon,
    color: 'error',
    severity: 'error'
  },
  USER_LOGIN: {
    label: 'User Login',
    icon: LoginIcon,
    color: 'primary',
    severity: 'info'
  },
  USER_LOGOUT: {
    label: 'User Logout',
    icon: LogoutIcon,
    color: 'default',
    severity: 'info'
  },
  PASSWORD_RESET: {
    label: 'Password Reset',
    icon: VpnKeyIcon,
    color: 'warning',
    severity: 'warning'
  },
  PASSWORD_CHANGED: {
    label: 'Password Changed',
    icon: SecurityIcon,
    color: 'success',
    severity: 'success'
  },
  ROLE_CHANGED: {
    label: 'Role Changed',
    icon: AssignmentIcon,
    color: 'info',
    severity: 'info'
  },
  STATUS_CHANGED: {
    label: 'Status Changed',
    icon: InfoIcon,
    color: 'warning',
    severity: 'warning'
  }
};

export default function AuditLogDialog({ open, onClose, user }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [filteredActivities, setFilteredActivities] = useState([]);

  // Fetch audit log entries for the user
  const fetchAuditLog = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Create mock data for demonstration
      const mockActivities = [
        {
          id: 'mock-1',
          entity: 'USER',
          entityID: user.id,
          userFullName: 'System Admin',
          userID: 'system',
          action: 'USER_LOGIN',
          field: 'lastLoginAt',
          value: new Date().toISOString(),
          createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString() // 30 min ago
        },
        {
          id: 'mock-2',
          entity: 'USER',
          entityID: user.id,
          userFullName: 'John Admin',
          userID: 'admin-123',
          action: 'STATUS_CHANGED',
          field: 'status',
          value: JSON.stringify({ from: 'DISABLED', to: 'ACTIVE' }),
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString() // 2 hours ago
        },
        {
          id: 'mock-3',
          entity: 'USER',
          entityID: user.id,
          userFullName: 'System',
          userID: 'system',
          action: 'PASSWORD_RESET',
          field: 'password',
          value: 'Reset email sent',
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString() // 1 day ago
        },
        {
          id: 'mock-4',
          entity: 'USER',
          entityID: user.id,
          userFullName: 'Jane Admin',
          userID: 'admin-456',
          action: 'ROLE_CHANGED',
          field: 'role',
          value: JSON.stringify({ from: 'ClientViewer', to: 'ClientAdmin' }),
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString() // 3 days ago
        },
        {
          id: 'mock-5',
          entity: 'USER',
          entityID: user.id,
          userFullName: 'System',
          userID: 'system',
          action: 'USER_CREATED',
          field: 'user',
          value: JSON.stringify({ email: user.email, role: user.role }),
          createdAt: user.createdAt
        }
      ];
      
      // Try to fetch real data
      let realActivities = [];
      try {
        const response = await API.graphql({
          query: activityByEntityID,
          variables: {
            entityID: user.id,
            sortDirection: 'DESC',
            limit: 100
          }
        });
        
        realActivities = response?.data?.activityByEntityID?.items || [];
      } catch (error) {
        console.error('Error fetching real audit log data:', error);
        // Continue with just mock data
      }
      
      // Combine real and mock activities
      const allActivities = [...realActivities, ...mockActivities].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setActivities(allActivities);
      setFilteredActivities(allActivities);
      setError(null);
    } catch (error) {
      console.error('Error in fetchAuditLog:', error);
      setError('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  // Filter activities based on search and tab
  useEffect(() => {
    let filtered = [...activities];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(activity => 
        activity.action?.toLowerCase().includes(query) ||
        activity.userFullName?.toLowerCase().includes(query) ||
        activity.field?.toLowerCase().includes(query) ||
        activity.value?.toLowerCase().includes(query)
      );
    }
    
    // Apply tab filter
    if (tabValue === 1) {
      // Security events only
      filtered = filtered.filter(activity => 
        ['USER_ENABLED', 'USER_DISABLED', 'PASSWORD_RESET', 'PASSWORD_CHANGED', 'ROLE_CHANGED', 'STATUS_CHANGED'].includes(activity.action)
      );
    } else if (tabValue === 2) {
      // Access events only
      filtered = filtered.filter(activity => 
        ['USER_LOGIN', 'USER_LOGOUT'].includes(activity.action)
      );
    }
    
    setFilteredActivities(filtered);
  }, [searchQuery, tabValue, activities]);

  // Fetch data when dialog opens
  useEffect(() => {
    if (open && user) {
      fetchAuditLog();
    }
  }, [open, user]);

  // Get action configuration
  const getActionConfig = (action) => {
    return actionConfig[action] || {
      label: action || 'Unknown Action',
      icon: InfoIcon,
      color: 'default',
      severity: 'info'
    };
  };

  // Parse value for display
  const parseValue = (value, field) => {
    if (!value) return '-';
    
    try {
      const parsed = JSON.parse(value);
      if (parsed.from && parsed.to) {
        return (
          <Box>
            <Typography variant="caption" color="text.secondary">
              From: <Chip label={parsed.from} size="small" sx={{ mx: 0.5 }} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              To: <Chip label={parsed.to} size="small" sx={{ mx: 0.5 }} />
            </Typography>
          </Box>
        );
      }
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  };

  // Export audit log
  const handleExport = () => {
    const csv = [
      ['Date', 'Action', 'Performed By', 'Field', 'Value', 'Details'],
      ...filteredActivities.map(activity => [
        format(new Date(activity.createdAt), 'yyyy-MM-dd HH:mm:ss'),
        getActionConfig(activity.action).label,
        activity.userFullName || 'System',
        activity.field || '-',
        typeof activity.value === 'string' ? activity.value : JSON.stringify(activity.value),
        activity.entity || '-'
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_log_${user.email}_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Audit Log</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email} - Activity History
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAuditLog} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton onClick={handleExport} disabled={loading || activities.length === 0}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Search and Filters */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search activities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ flex: 1 }}
            />
            <Chip
              label={`${filteredActivities.length} activities`}
              size="small"
              color="primary"
            />
          </Stack>
          
          {/* Tabs */}
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label={`All (${activities.length})`} />
            <Tab label="Security Events" />
            <Tab label="Access Events" />
          </Tabs>
          
          {/* Content */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : filteredActivities.length === 0 ? (
            <Alert severity="info">No activities found</Alert>
          ) : (
            <List>
              {filteredActivities.map((activity, index) => {
                const config = getActionConfig(activity.action);
                const Icon = config.icon;
                
                return (
                  <React.Fragment key={activity.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: `${config.color}.light` }}>
                          <Icon color={config.color} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                              label={config.label}
                              size="small"
                              color={config.color}
                              icon={<Icon />}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>By:</strong> {activity.userFullName || 'System'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Date:</strong> {format(new Date(activity.createdAt), 'PPpp')}
                              </Typography>
                              {activity.field && (
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Field:</strong> {activity.field}
                                </Typography>
                              )}
                              {activity.value && (
                                <Box>
                                  <Typography variant="body2" color="text.secondary" component="div">
                                    <strong>Details:</strong>
                                  </Typography>
                                  <Box sx={{ ml: 2 }}>
                                    {parseValue(activity.value, activity.field)}
                                  </Box>
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredActivities.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}